// src/components/user/StewardWorkspace.jsx
// Desktop Steward Workspace: per-boundary tabs (or, past AREA_TABS_MAX areas, a searchable area
// picker — Roadmap #86), listing table with edit/remove/restore/add.
// Reachable from ProfileMenu for Stewards and platform admins.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { isPlatformAdmin } from '../../utils/adminHelpers';
import StandardButton from '../core/StandardButton';
import { tokens } from '../../styles/designTokens';
import {
  getStewardWorkspaceAreas,
  getStewardAppointments,
  getBoundaryParents,
  getListingsForSteward,
  getNationalListings,
  getNationalListingCountries,
  createListing,
  updateListing,
  softDeleteListing,
  restoreListing
} from '../../data/directoryService';
import { groupService, communityService } from '../../data/dataService';
import ConfirmationModal from '../core/ConfirmationModal';
import ReconcileLinkModal from './ReconcileLinkModal';
import TagInput from '../shared/TagInput';
import StewardAreaPicker from './StewardAreaPicker';
import { AREA_TABS_MAX, pickStartingArea, stewardNamesByArea, joinNames, groupAreasByMunicipality, appointedAmong, countriesOf, countryName, NATIONAL, listingAreaWrite } from '../../utils/stewardAreas';

// The area this person last chose here, per browser (R3). Written only on an explicit choice.
const LAST_AREA_KEY_PREFIX = 'ournet_steward_workspace_area:';
const readLastArea = (userId) => {
  try { return userId ? window.localStorage.getItem(LAST_AREA_KEY_PREFIX + userId) : null; } catch { return null; }
};
const writeLastArea = (userId, boundaryId) => {
  try { if (userId) window.localStorage.setItem(LAST_AREA_KEY_PREFIX + userId, String(boundaryId)); } catch { /* not kept */ }
};

const SECTION_OPTIONS = [
  { value: 'official_contact', labelKey: 'section_official_contact' },
  { value: 'utility', labelKey: 'section_utility' },
  { value: 'directory', labelKey: 'section_directory' }
];

const CATEGORY_OPTIONS = {
  directory: [
    { value: 'health', labelKey: 'dir_health' },
    { value: 'home', labelKey: 'dir_home' },
    { value: 'official', labelKey: 'dir_official' },
    { value: 'learning', labelKey: 'dir_learning' },
    { value: 'food_shopping', labelKey: 'dir_food_shopping' },
    { value: 'activities', labelKey: 'dir_activities' },
    { value: 'transport', labelKey: 'dir_transport' },
    { value: 'eating_drinking', labelKey: 'dir_eating_drinking' }
  ],
  utility: [
    { value: 'electricity', labelKey: 'utility_electricity' },
    { value: 'water', labelKey: 'utility_water' },
    { value: 'gas', labelKey: 'utility_gas' },
    { value: 'internet_phone', labelKey: 'utility_internet' }
  ],
  official_contact: [
    { value: 'junta', labelKey: 'contact_junta' },
    { value: 'camara', labelKey: 'contact_camara' },
    { value: 'health_centre', labelKey: 'contact_health_centre' }
  ]
};

const CLAIM_BADGES = {
  unclaimed: { color: '#6b7280', bg: '#f3f4f6' },
  pending:   { color: '#d97706', bg: '#fef3c7' },
  verified:  { color: '#059669', bg: '#ecfdf5' }
};

// Directory category → seeded group tag (community_tags). community_type stays 'group';
// the directory category becomes a searchable tag on the new group. Directory-section
// categories only — other sections seed no tag.
const CATEGORY_TAG_MAP = {
  health: 'Health',
  home: 'Home services',
  official: 'Official',
  learning: 'Learning',
  food_shopping: 'Food & shopping',
  activities: 'Activities',
  transport: 'Transport',
  eating_drinking: 'Eating & drinking'
};

// ---------------------------------------------------------------------------
// Listing edit/add modal (inline)
// ---------------------------------------------------------------------------
const ListingFormModal = ({ listing, boundaryId, nationalCode = null, nationalLabel = null, areas = [], locale, onClose, onSaved, tS, tF }) => {
  const isEdit = Boolean(listing);
  // R5 (Roadmap #87): the listing's area, among the areas this person covers. A group-linked
  // listing takes its area from the group (syncLinkedListing), so a move here would be pulled
  // back at the next group save — the field is shown but locked.
  // National (addendum 2, admin only): a national listing, or one added from a "National" entry,
  // starts on its country; choosing an area instead unmarks it (area and country change in one
  // write — the database allows exactly one). Only that country's areas are offered. Marking an
  // area listing national is not offered here (R7's reviewed data fix does it).
  const isNational = Boolean(listing?.national_country_code);
  const isLinked = Boolean(listing?.claimed_by_group_id);
  const country = isEdit ? (listing.national_country_code || null) : nationalCode;
  const startArea = country ? NATIONAL : Number(listing?.boundary_id ?? boundaryId);
  const [areaId, setAreaId] = useState(startArea);
  const offeredAreas = useMemo(
    () => (country ? areas.filter(a => a.countryCode === country) : areas),
    [areas, country]
  );
  const areaGroups = useMemo(() => groupAreasByMunicipality(offeredAreas, locale), [offeredAreas, locale]);
  const [form, setForm] = useState({
    section: listing?.section || 'directory',
    category: listing?.category || '',
    name: listing?.name || '',
    description: listing?.description || '',
    phone: listing?.phone || '',
    email: listing?.email || '',
    website: listing?.website || '',
    address: listing?.address || ''
  });
  const [tags, setTags] = useState(listing?.tags || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(tS('name_required')); return; }
    if (!form.section) { setError(tS('section_required')); return; }

    setSaving(true);
    setError(null);

    const payload = { ...form };
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    payload.tags = tags;

    const { fields, where } = listingAreaWrite({
      isEdit, isNational, isLinked, country, areaId, startArea, listingAreaId: listing?.boundary_id
    });
    Object.assign(payload, fields);
    const result = isEdit ? await updateListing(listing.id, payload) : await createListing(payload);

    setSaving(false);
    if (result.success) {
      onSaved(where);
    } else {
      setError(result.error?.message || tS('save_error'));
    }
  };

  const categories = CATEGORY_OPTIONS[form.section] || [];

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '3px',
    display: 'block'
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{
        position: 'relative', background: 'white', borderRadius: '12px', padding: '24px',
        width: '90%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)', fontFamily: 'OpenSauceSans, sans-serif'
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#024B99', marginTop: 0, marginBottom: '16px' }}>
          {isEdit ? tS('edit_title') : tS('add_title')}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Area (R5); for a national listing, its country first (addendum 2) */}
          {(country || areaGroups.length > 0) && (
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{tS('area_picker_label')}</label>
              <select
                value={areaId}
                onChange={(e) => setAreaId(e.target.value === NATIONAL ? NATIONAL : Number(e.target.value))}
                style={inputStyle}
                disabled={isLinked}
              >
                {country && <option value={NATIONAL}>{nationalLabel}</option>}
                {areaGroups.map(group => (
                  <optgroup key={group.key} label={group.heading}>
                    {group.areas.map(a => (
                      <option key={a.boundary_id} value={Number(a.boundary_id)}>{a.boundary_name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {isLinked && (
                <div style={{ fontSize: '11px', color: tokens.colors.textSecondary, marginTop: '4px' }}>{tS('area_linked_note')}</div>
              )}
            </div>
          )}

          {/* Section */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tS('col_section')}</label>
            <select value={form.section} onChange={(e) => { handleChange('section', e.target.value); handleChange('category', ''); }} style={inputStyle} disabled={isEdit}>
              {SECTION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{tF(opt.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Category */}
          {categories.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{tS('col_category')}</label>
              <select value={form.category} onChange={(e) => handleChange('category', e.target.value)} style={inputStyle}>
                <option value="">—</option>
                {categories.map(opt => (
                  <option key={opt.value} value={opt.value}>{tF(opt.labelKey)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tS('col_name')} *</label>
            <input type="text" value={form.name} onChange={(e) => handleChange('name', e.target.value)} style={inputStyle} />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tS('col_phone')}</label>
            <input type="tel" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} style={inputStyle} />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tS('col_email')}</label>
            <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} style={inputStyle} />
          </div>

          {/* Website */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tF('suggest_website')}</label>
            <input type="url" value={form.website} onChange={(e) => handleChange('website', e.target.value)} style={inputStyle} />
          </div>

          {/* Address */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tF('suggest_address')}</label>
            <input type="text" value={form.address} onChange={(e) => handleChange('address', e.target.value)} style={inputStyle} />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{tS('description_label')}</label>
            <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
          </div>

          {/* Tags */}
          {form.section === 'directory' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{tF('suggest_tags')}</label>
              <TagInput
                tags={tags}
                onAdd={(newTags) => setTags(prev => [...prev, ...newTags])}
                onRemove={(tag) => setTags(prev => prev.filter(t2 => t2 !== tag))}
                max={5}
                caseMode="lower"
                singleWord={true}
                placeholder={tF('suggest_tags_placeholder')}
                addLabel={tF('suggest_tags_add')}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '12px', color: '#dc2626' }}>
              {error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
              background: 'white', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#6b7280', cursor: 'pointer'
            }}>
              {tF('common:cancel')}
            </button>
            <button type="submit" disabled={saving} style={{
              padding: '8px 16px', border: 'none', borderRadius: '6px',
              background: saving ? '#93c5fd' : '#024B99', fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px', fontWeight: '600', color: 'white', cursor: saving ? 'default' : 'pointer'
            }}>
              {saving ? tS('saving') : tS('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section-grouped listings table
// ---------------------------------------------------------------------------

// Display order for sections — Directory first, then Official Contacts, then Utilities
const SECTION_DISPLAY_ORDER = ['directory', 'official_contact', 'utility'];

// Section heading i18n keys (frontpage namespace)
const SECTION_HEADING_KEYS = {
  directory: 'directory',
  official_contact: 'official_contacts',
  utility: 'utility_contacts'
};

// Category display order within Directory
const DIRECTORY_CATEGORY_ORDER = CATEGORY_OPTIONS.directory.map(c => c.value);

const SectionGroupedTable = ({
  listings, cellStyle, headerCellStyle, btnSmall,
  tS, tF, sectionLabel, categoryLabel, claimLabel,
  onEdit, onRemove, onRestore, onCreateGroup, onLink
}) => {
  // Group listings by section
  const bySection = {};
  listings.forEach(row => {
    const s = row.section || 'directory';
    if (!bySection[s]) bySection[s] = [];
    bySection[s].push(row);
  });

  // Ordered sections that have at least one row
  const activeSections = SECTION_DISPLAY_ORDER.filter(s => bySection[s]?.length > 0);

  // For directory, sub-group by category
  const directorByCategory = (rows) => {
    const byCat = {};
    rows.forEach(row => {
      const c = row.category || '_other';
      if (!byCat[c]) byCat[c] = [];
      byCat[c].push(row);
    });
    // Ordered categories, then any unknown ones
    const orderedCats = DIRECTORY_CATEGORY_ORDER.filter(c => byCat[c]?.length > 0);
    Object.keys(byCat).forEach(c => {
      if (!orderedCats.includes(c)) orderedCats.push(c);
    });
    return orderedCats.map(c => ({ category: c, rows: byCat[c] }));
  };

  const sectionHeadingStyle = {
    fontFamily: 'OpenSauceSans, sans-serif',
    fontWeight: '700',
    fontSize: '15px',
    color: '#024B99',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '16px 0 8px'
  };

  const categoryHeadingStyle = {
    fontFamily: 'OpenSauceSans, sans-serif',
    fontWeight: '600',
    fontSize: '13px',
    color: '#6b7280',
    padding: '10px 0 4px'
  };

  const sectionDividerStyle = {
    borderTop: '2px solid #d1d5db',
    marginTop: '8px'
  };

  const categoryDividerStyle = {
    borderTop: '1px solid #e5e7eb',
    marginTop: '4px'
  };

  const renderRow = (row) => {
    const inactive = !row.is_active;
    const rowStyle = inactive ? { opacity: 0.55 } : {};
    const claim = CLAIM_BADGES[row.claim_status] || CLAIM_BADGES.unclaimed;

    return (
      <tr key={row.id} style={rowStyle}>
        <td style={cellStyle}>
          <div style={{ fontWeight: '600' }}>{row.name}</div>
          {row.tags && row.tags.length > 0 && (
            <div style={{ marginTop: '3px' }}>
              {row.tags.map(tag => (
                <span key={tag} style={{
                  display: 'inline-block', padding: '1px 6px', marginRight: '4px',
                  borderRadius: '4px', fontSize: '11px', background: '#e0e7ff', color: '#3730a3'
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </td>
        <td style={cellStyle}>{row.phone || '—'}</td>
        <td style={{ ...cellStyle, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.email || '—'}
        </td>
        <td style={cellStyle}>
          {inactive ? (
            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: '#fef2f2', color: '#dc2626' }}>
              {tS('status_inactive')}
            </span>
          ) : (
            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', background: '#ecfdf5', color: '#059669' }}>
              {tS('status_active')}
            </span>
          )}
        </td>
        <td style={cellStyle}>
          <span style={{
            padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
            fontWeight: '600', background: claim.bg, color: claim.color
          }}>
            {claimLabel(row.claim_status)}
          </span>
        </td>
        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
          {inactive ? (
            <button
              onClick={() => onRestore(row)}
              style={{ ...btnSmall, background: '#ecfdf5', color: '#059669' }}
            >
              {tS('restore')}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => onEdit(row)}
                style={{ ...btnSmall, background: '#eff6ff', color: '#024B99' }}
              >
                {tS('common:edit')}
              </button>
              <button
                onClick={() => onRemove(row)}
                style={{ ...btnSmall, background: '#fef2f2', color: '#dc2626' }}
              >
                {tS('remove')}
              </button>
              {/* Directory→group linking — only when not already linked. */}
              {!row.claimed_by_group_id && (
                <>
                  <button
                    onClick={() => onCreateGroup(row)}
                    style={{ ...btnSmall, background: '#f5f3ff', color: '#7c3aed' }}
                  >
                    {tS('create_group_from_listing')}
                  </button>
                  <button
                    onClick={() => onLink(row)}
                    style={{ ...btnSmall, background: '#f5f3ff', color: '#7c3aed' }}
                  >
                    {tS('link_existing_group')}
                  </button>
                </>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  const tableHeader = (
    <thead>
      <tr>
        <th style={headerCellStyle}>{tS('col_name')}</th>
        <th style={headerCellStyle}>{tS('col_phone')}</th>
        <th style={headerCellStyle}>{tS('col_email')}</th>
        <th style={headerCellStyle}>{tS('col_status')}</th>
        <th style={headerCellStyle}>{tS('col_claim')}</th>
        <th style={headerCellStyle}>{tS('col_actions')}</th>
      </tr>
    </thead>
  );

  return (
    <div>
      {activeSections.map((section, sIdx) => {
        const rows = bySection[section];
        const headingKey = SECTION_HEADING_KEYS[section] || section;
        const isDirectory = section === 'directory';
        const categoryGroups = isDirectory ? directorByCategory(rows) : null;

        return (
          <div key={section} style={sIdx > 0 ? sectionDividerStyle : undefined}>
            {/* Section heading */}
            <div style={sectionHeadingStyle}>
              {tF(headingKey)}
            </div>

            {isDirectory && categoryGroups ? (
              /* Directory: sub-grouped by category */
              categoryGroups.map((group, cIdx) => (
                <div key={group.category} style={cIdx > 0 ? categoryDividerStyle : undefined}>
                  {/* Category sub-heading */}
                  <div style={categoryHeadingStyle}>
                    {group.category === '_other'
                      ? tF('dir_other')
                      : tF(`dir_${group.category}`)
                    }
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      {cIdx === 0 && tableHeader}
                      <tbody>
                        {group.rows.map(renderRow)}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            ) : (
              /* Official Contacts / Utilities: flat list */
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {tableHeader}
                  <tbody>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main StewardWorkspace component
// ---------------------------------------------------------------------------
const StewardWorkspace = ({ currentUser, onBack }) => {
  const { t: tS, formattingLocale } = useTranslation('steward');
  const { t: tF } = useTranslation('frontpage');
  const theme = useTheme();

  const isAdmin = isPlatformAdmin(currentUser);

  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  // An admin's "National" entry (Roadmap #87, addendum 2): the country whose national listings are
  // shown, held apart from activeTab so the area paths stay numeric. Not remembered (R3's stored
  // choice is an area); the page opens on an area as before.
  const [nationalCode, setNationalCode] = useState(null);
  const [nationalCountries, setNationalCountries] = useState([]);
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [formModal, setFormModal] = useState(null); // null | { listing?, boundaryId }
  const [removeModal, setRemoveModal] = useState({ visible: false, listing: null });
  const [linkPicker, setLinkPicker] = useState(null); // null | { listing, groups, loading }
  const [reconcile, setReconcile] = useState(null);   // null | { listing, group }
  const [message, setMessage] = useState(null);

  // Desktop-only guard. Same-value functional-update guard as useMediaQuery.js's
  // Phase 5a fix, applied here for consistency (Phase 5b §7) — a second,
  // independent instance of the same resize-listener pattern.
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1200);
  useEffect(() => {
    const handler = () => setIsDesktop(prev => {
      const next = window.innerWidth >= 1200;
      return prev === next ? prev : next;
    });
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Load areas (Roadmap #86). For an admin: every area with a listing, plus every area with a
  // steward appointed (addendum 1, ruling 2), each with all its stewards by name (ruling 4) —
  // the function's single steward_name is not used. For a steward: every area inside each
  // appointment, as the function returns them (Roadmap #87, R4). Every area carries its tier and
  // parent for the picker's municipality groups (ruling 3; Roadmap #87).
  // Only the list loads here; listings load for the chosen area alone (R4).
  useEffect(() => {
    (async () => {
      setLoadingAreas(true);
      const [rows, appointments, nationalHeld] = await Promise.all([
        getStewardWorkspaceAreas(),
        isAdmin ? getStewardAppointments() : Promise.resolve([]),
        isAdmin ? getNationalListingCountries() : Promise.resolve([])
      ]);
      const ids = [...new Set([
        ...rows.map(r => Number(r.boundary_id)),
        ...appointments.map(a => a.boundary_id)
      ])];
      const places = await getBoundaryParents(ids);
      const namesByArea = stewardNamesByArea(appointments, formattingLocale);
      const fromRpc = Object.fromEntries(rows.map(r => [Number(r.boundary_id), r]));
      const list = ids
        .map(id => ({
          boundary_id: id,
          boundary_name: fromRpc[id]?.boundary_name || places[id]?.name || String(id),
          tier: places[id]?.tier ?? null,
          parentId: places[id]?.parentId ?? null,
          parentName: places[id]?.parentName || null,
          countryCode: places[id]?.countryCode || null,
          stewards: isAdmin ? (namesByArea[id] || []) : (fromRpc[id]?.steward_name ? [fromRpc[id].steward_name] : [])
        }))
        .sort((a, b) => a.boundary_name.localeCompare(b.boundary_name, formattingLocale));
      // R3: last chosen, else an area they steward by appointment, else none. A steward is
      // offered every area inside each appointment (Roadmap #87), so their appointments are the
      // offered areas whose parent is not offered; an admin's own are the rows carrying their
      // own user id.
      const ownIds = isAdmin
        ? appointments.filter(a => a.user_id === currentUser?.id).map(a => a.boundary_id)
        : appointedAmong(list);
      setAreas(list);
      if (isAdmin) setNationalCountries(countriesOf(list, nationalHeld));
      setActiveTab(pickStartingArea(list, readLastArea(currentUser?.id), ownIds));
      setLoadingAreas(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An explicit choice: show that area and remember it for next time.
  const chooseArea = (boundaryId) => {
    setNationalCode(null);
    setActiveTab(boundaryId);
    writeLastArea(currentUser?.id, boundaryId);
  };

  const chooseNational = (code) => setNationalCode(code);

  const nationalLabel = (code) => tS('national_entry', { country: countryName(code, formattingLocale) });
  const nationalEntries = nationalCountries.map(code => ({ key: code, label: nationalLabel(code) }));

  const stewardLabel = (area) => {
    const names = area?.stewards || [];
    if (names.length === 0) return tS('unassigned');
    const list = joinNames(names, formattingLocale);
    return names.length === 1 ? tS('steward_of_record', { name: list }) : tS('stewards_of_record', { names: list });
  };

  // Load the listings in view: a country's national ones, or the chosen area's. Only the latest
  // request lands, so a slow earlier read never overwrites the view chosen after it.
  const latestLoad = useRef(0);
  const loadListings = useCallback(async (boundaryId, country) => {
    if (!country && !boundaryId) return;
    const ticket = ++latestLoad.current;
    setLoadingListings(true);
    const data = country ? await getNationalListings(country) : await getListingsForSteward(boundaryId);
    if (ticket !== latestLoad.current) return;
    setListings(data);
    setLoadingListings(false);
  }, []);

  const reload = () => loadListings(activeTab, nationalCode);

  useEffect(() => {
    loadListings(activeTab, nationalCode);
  }, [activeTab, nationalCode, loadListings]);

  const handleRemove = async () => {
    const listing = removeModal.listing;
    if (!listing) return;
    setRemoveModal({ visible: false, listing: null });
    const result = await softDeleteListing(listing.id);
    if (result.success) {
      setMessage({ type: 'success', text: tS('save_success') });
      reload();
    } else {
      setMessage({ type: 'error', text: tS('save_error') });
    }
  };

  const handleRestore = async (listing) => {
    const result = await restoreListing(listing.id);
    if (result.success) {
      setMessage({ type: 'success', text: tS('save_success') });
      reload();
    } else {
      setMessage({ type: 'error', text: tS('save_error') });
    }
  };

  // A listing saved to another area (R5), or unmarked from national (addendum 2), shows there,
  // so a move never reads as a removal. Moving it is the person's explicit choice of that area,
  // so it is remembered like one (R3). One saved as national shows in its country's list.
  const handleFormSaved = (where) => {
    setFormModal(null);
    setMessage({ type: 'success', text: tS('save_success') });
    if (where && typeof where === 'object') {
      if (where.national !== nationalCode) chooseNational(where.national);
      else reload();
    } else if (where != null && (nationalCode || where !== activeTab)) {
      chooseArea(where);
    } else {
      reload();
    }
  };

  // Directory → group: prefill the create-group flow from a listing. The Steward workspace is
  // itself steward/admin-gated, so a group created here is Steward-authored → claim 'verified'.
  // The group id is written back onto the listing once the group saves (AddEditGroup, task 1).
  // Directory → existing group: pick one of the Steward's own groups, then reconcile. The picker
  // is scoped to owned groups because the reconciliation may write to the group (owner/admin RLS).
  const handleLinkExisting = async (row) => {
    setLinkPicker({ listing: row, groups: [], loading: true });
    const owned = await groupService.getOwnedGroups(currentUser?.id);
    const linkable = (owned || []).filter(g => !g.deleted_at && !g.deletion_scheduled_at);
    setLinkPicker({ listing: row, groups: linkable, loading: false });
  };

  const handlePickGroup = async (groupSummary) => {
    const listing = linkPicker?.listing;
    setLinkPicker(null);
    const fullGroup = await communityService.getCommunityById(groupSummary.id);
    if (!fullGroup) {
      setMessage({ type: 'error', text: tS('save_error') });
      return;
    }
    setReconcile({ listing, group: fullGroup });
  };

  const handleReconcileDone = () => {
    setReconcile(null);
    setMessage({ type: 'success', text: tS('save_success') });
    reload();
  };

  const handleCreateGroupFromListing = (row) => {
    const tag = CATEGORY_TAG_MAP[row.category];
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: {
        view: 'new-group',
        prefill: {
          name: row.name || '',
          description: row.description || '',
          boundary_ids: row.boundary_id ? [row.boundary_id] : [],
          community_tags: tag ? [tag] : [],
          link: { listingId: row.id, claimStatus: 'verified' }
        }
      }
    }));
  };

  // Clear message after 3 seconds
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  const claimLabel = (status) => {
    if (status === 'pending') return tS('claim_pending');
    if (status === 'verified') return tS('claim_verified');
    return tS('claim_unclaimed');
  };

  const sectionLabel = (section) => {
    const opt = SECTION_OPTIONS.find(o => o.value === section);
    return opt ? tF(opt.labelKey) : section;
  };

  const categoryLabel = (section, category) => {
    if (!category) return '—';
    const cats = CATEGORY_OPTIONS[section];
    if (!cats) return category;
    const opt = cats.find(c => c.value === category);
    return opt ? tF(opt.labelKey) : category;
  };

  // Desktop-only gate
  if (!isDesktop) {
    return (
      <div style={{
        padding: '80px 20px 20px', textAlign: 'center',
        fontFamily: 'OpenSauceSans, sans-serif', fontSize: '14px', color: '#6b7280'
      }}>
        <p>{tS('desktop_only')}</p>
        <div style={{ marginTop: '16px', display: 'inline-block' }}>
          <StandardButton variant="context" iconName="ArrowLeft" label={tS('back')} onClick={onBack} />
        </div>
      </div>
    );
  }

  // Loading
  if (loadingAreas) {
    return (
      <div style={{ padding: '100px 20px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '14px', color: '#6b7280' }}>
        {tS('loading')}
      </div>
    );
  }

  // No areas (an admin with a country's national listings still has somewhere to go)
  if (areas.length === 0 && nationalEntries.length === 0) {
    return (
      <div style={{
        padding: '100px 20px', textAlign: 'center',
        fontFamily: 'OpenSauceSans, sans-serif', fontSize: '14px', color: '#6b7280'
      }}>
        <p>{tS('no_areas')}</p>
        <div style={{ marginTop: '16px', display: 'inline-block' }}>
          <StandardButton variant="context" iconName="ArrowLeft" label={tS('back')} onClick={onBack} />
        </div>
      </div>
    );
  }

  // No fallback to the first area: with nothing chosen, nothing shows or loads (R3).
  const activeArea = nationalCode ? null : (areas.find(a => a.boundary_id === activeTab) || null);
  const inView = Boolean(nationalCode || activeArea); // something chosen: a country's national list, or an area
  const usePicker = areas.length > AREA_TABS_MAX;
  const contextColour = theme?.primary || tokens.colors.contextLocality; // LAYOUT: never a hardcoded context hex
  // Tabs: an admin's national entries first, then the areas (one rule with the picker).
  const tabEntries = [
    ...nationalEntries.map(e => ({ key: `national-${e.key}`, label: e.label, active: nationalCode === e.key, onChoose: () => chooseNational(e.key), area: null })),
    ...areas.map(area => ({ key: area.boundary_id, label: area.boundary_name, active: !nationalCode && activeTab === area.boundary_id, onChoose: () => chooseArea(area.boundary_id), area }))
  ];

  const cellStyle = {
    padding: '8px 10px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'top'
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: '600',
    fontSize: '12px',
    color: '#6b7280',
    background: '#f9fafb',
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap'
  };

  const btnSmall = {
    padding: '4px 10px',
    border: 'none',
    borderRadius: '5px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  };

  return (
    <div style={{ padding: '90px 24px 40px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <StandardButton variant="context" iconName="ArrowLeft" label={tS('back')} onClick={onBack} />
        <h1 style={{
          fontSize: '20px', fontWeight: '700', color: theme?.text?.primary || '#1f2937',
          fontFamily: 'OpenSauceSans, sans-serif', margin: 0
        }}>
          {tS('workspace_title')}
        </h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
          <StandardButton
            variant="utility"
            label={tS('import_events_link')}
            onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'events-importer' } }))}
          />
          <StandardButton
            variant="utility"
            label={tS('pending_events_link')}
            onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'events-pending' } }))}
          />
        </div>
      </div>

      {/* Message banner */}
      {message && (
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px',
          background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color: message.type === 'success' ? '#059669' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Many areas: one searchable picker in place of the tabs (R2). */}
      {usePicker && (
        <StewardAreaPicker
          areas={areas}
          activeId={nationalCode ? null : activeTab}
          onChoose={chooseArea}
          showStewards={isAdmin}
          extraEntries={nationalEntries}
          activeExtraKey={nationalCode}
          onChooseExtra={chooseNational}
        />
      )}

      {/* Tabs (more than one area, up to AREA_TABS_MAX); the row scrolls sideways when it
          overflows (R1). */}
      {!usePicker && (tabEntries.length > 1 || nationalEntries.length > 0) && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0', overflowX: 'auto' }}>
          {tabEntries.map(entry => (
            <button
              key={entry.key}
              onClick={entry.onChoose}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderBottom: entry.active ? `3px solid ${contextColour}` : '3px solid transparent',
                background: entry.active ? '#eff6ff' : 'transparent',
                fontFamily: 'OpenSauceSans, sans-serif',
                fontSize: '13px',
                fontWeight: entry.active ? '700' : '500',
                color: entry.active ? contextColour : '#6b7280',
                cursor: 'pointer',
                marginBottom: '-2px',
                borderRadius: '6px 6px 0 0',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
            >
              <div>{entry.label}</div>
              {isAdmin && entry.area && (
                <div style={{ fontSize: '11px', fontWeight: '400', color: '#9ca3af', marginTop: '2px' }}>
                  {stewardLabel(entry.area)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Single-area steward-of-record line (when only one tab) */}
      {areas.length === 1 && isAdmin && activeArea && (
        <div style={{
          marginBottom: '12px', fontFamily: 'OpenSauceSans, sans-serif',
          fontSize: '13px', color: '#6b7280'
        }}>
          {stewardLabel(activeArea)}
        </div>
      )}

      {/* Add listing button — only once an area (or a country's national list) is chosen */}
      {inView && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            onClick={() => setFormModal({ listing: null, boundaryId: nationalCode ? null : activeTab, nationalCode })}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: '6px',
              background: '#059669', color: 'white', fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '13px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            + {tS('add_listing')}
          </button>
        </div>
      )}

      {/* Listings table — grouped by section, directory sub-grouped by category. With no area
          chosen yet (R3), nothing loads; the tabs ask for a choice (the picker asks itself). */}
      {!inView ? (
        !usePicker && (
          <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#9ca3af' }}>
            {tS('area_picker_choose')}
          </div>
        )
      ) : loadingListings ? (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#9ca3af' }}>
          {tS('loading')}
        </div>
      ) : listings.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#9ca3af' }}>
          —
        </div>
      ) : (
        <SectionGroupedTable
          listings={listings}
          cellStyle={cellStyle}
          headerCellStyle={headerCellStyle}
          btnSmall={btnSmall}
          tS={tS}
          tF={tF}
          sectionLabel={sectionLabel}
          categoryLabel={categoryLabel}
          claimLabel={claimLabel}
          onEdit={(row) => setFormModal({ listing: row, boundaryId: activeTab })}
          onRemove={(row) => setRemoveModal({ visible: true, listing: row })}
          onRestore={handleRestore}
          onCreateGroup={handleCreateGroupFromListing}
          onLink={handleLinkExisting}
        />
      )}

      {/* Remove confirmation */}
      <ConfirmationModal
        visible={removeModal.visible}
        title={tS('remove')}
        message={removeModal.listing ? tS('confirm_remove') : ''}
        okLabel={tS('remove')}
        cancelLabel={tF('common:cancel')}
        danger={true}
        onOk={handleRemove}
        onCancel={() => setRemoveModal({ visible: false, listing: null })}
      />

      {/* Add/Edit modal */}
      {formModal && (
        <ListingFormModal
          listing={formModal.listing}
          boundaryId={formModal.boundaryId}
          nationalCode={formModal.nationalCode || null}
          nationalLabel={(formModal.listing?.national_country_code || formModal.nationalCode)
            ? nationalLabel(formModal.listing?.national_country_code || formModal.nationalCode)
            : null}
          areas={areas}
          locale={formattingLocale}
          onClose={() => setFormModal(null)}
          onSaved={handleFormSaved}
          tS={tS}
          tF={tF}
        />
      )}

      {/* Link-to-existing-group picker (owned groups only) */}
      {linkPicker && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={() => setLinkPicker(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div style={{
            position: 'relative', background: 'white', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '440px', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', fontFamily: 'OpenSauceSans, sans-serif'
          }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#024B99', marginTop: 0, marginBottom: '6px' }}>
              {tS('picker_title')}
            </h2>
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: 0, marginBottom: '16px' }}>
              {tS('picker_intro')}
            </p>
            {linkPicker.loading ? (
              <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#9ca3af' }}>{tS('loading')}</div>
            ) : linkPicker.groups.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', fontSize: '13px', color: '#9ca3af' }}>{tS('picker_empty')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {linkPicker.groups.map(g => (
                  <button
                    key={g.id}
                    onClick={() => handlePickGroup(g)}
                    style={{
                      textAlign: 'left', padding: '10px 12px', borderRadius: '8px',
                      border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer',
                      fontFamily: 'OpenSauceSans, sans-serif', fontSize: '14px', color: '#111827', fontWeight: '600'
                    }}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" onClick={() => setLinkPicker(null)} style={{
                padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
                background: 'white', fontFamily: 'OpenSauceSans, sans-serif', fontSize: '13px', color: '#6b7280', cursor: 'pointer'
              }}>
                {tF('common:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation modal (link-existing path) */}
      {reconcile && (
        <ReconcileLinkModal
          listing={reconcile.listing}
          group={reconcile.group}
          onClose={() => setReconcile(null)}
          onDone={handleReconcileDone}
        />
      )}
    </div>
  );
};

export default StewardWorkspace;
